import { useEffect } from 'react';

export interface IUseSyncScrollOptions {
  /** Root element containing both editor groups (identified by `data-gid`). */
  rootRef: React.RefObject<HTMLElement | null>;
  /** Only attaches while true (e.g. `state.link && pairComplete`). */
  enabled: boolean;
  groupIdA: string | undefined;
  groupIdB: string | undefined;
  /** Re-attach when the open file in either pane changes — the viewer DOM
   * (and its scroll container) gets recreated, so the old listeners are stale. */
  fileIdA?: string | null;
  fileIdB?: string | null;
}

/**
 * Ratio-syncs scroll position between the two panes forming the active A ↔ B
 * pair, while `enabled`. Finds the widest scrollable descendant under each
 * `[data-gid="<id>"]` group and scroll-broadcasts between them — independent
 * of diff (see PLAN-library-integration.md, Faza 2/3: the "link" toggle works
 * regardless of whether redline is on).
 */
export function useSyncScroll({
  rootRef,
  enabled,
  groupIdA,
  groupIdB,
  fileIdA,
  fileIdB,
}: IUseSyncScrollOptions): void {
  useEffect(() => {
    if (!enabled || !groupIdA || !groupIdB) return;
    const root = rootRef.current;
    if (!root) return;

    const scrollerIn = (gid: string) => {
      const g = root.querySelector<HTMLElement>(`[data-gid="${gid}"]`);
      if (!g) return null;
      const cands = Array.from(g.querySelectorAll<HTMLElement>('*')).filter(el => {
        const s = getComputedStyle(el);
        return (
          (s.overflowY === 'auto' || s.overflowY === 'scroll') &&
          el.scrollHeight > el.clientHeight + 4
        );
      });
      return cands.sort((x, y) => y.clientWidth - x.clientWidth)[0] ?? null;
    };

    let scrollers: HTMLElement[] = [];
    let lock = false;
    const onScroll = (e: Event) => {
      if (lock) return;
      lock = true;
      const src = e.currentTarget as HTMLElement;
      const ratio = src.scrollTop / Math.max(1, src.scrollHeight - src.clientHeight);
      scrollers.forEach(o => {
        if (o !== src) o.scrollTop = ratio * (o.scrollHeight - o.clientHeight);
      });
      requestAnimationFrame(() => {
        lock = false;
      });
    };

    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const attach = () => {
      scrollers = [scrollerIn(groupIdA), scrollerIn(groupIdB)].filter(Boolean) as HTMLElement[];
      if (scrollers.length < 2 && tries++ < 20) {
        timer = setTimeout(attach, 250);
        return;
      }
      scrollers.forEach(s => s.addEventListener('scroll', onScroll));
    };
    attach();

    return () => {
      clearTimeout(timer);
      scrollers.forEach(s => s.removeEventListener('scroll', onScroll));
    };
  }, [enabled, groupIdA, groupIdB, fileIdA, fileIdB, rootRef]);
}
